import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { N0DES_API_PREFIX } from "@/api/domain"
import type { UserAuthSignup } from "@/api/routes/users"
import type {
  Pagination,
  ProjectIdParam,
  ProjectInvite,
  ProjectInviteItem,
  ProjectRole,
  Uuid,
} from "@/types"

export type InvitesListResponse = {
  invites: ProjectInviteItem[]
  total: number
}

export type CreateProjectInviteRequest = {
  username_or_email: string
  role: ProjectRole
}

// Define a service using a base URL and expected endpoints
export const projectInvitesApi = createApi({
  reducerPath: "projectInvitesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${N0DES_API_PREFIX}project-invites`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    projectInviteGet: build.query<
      InvitesListResponse,
      ProjectIdParam & Pagination
    >({
      query: ({ project_id, offset, limit }) => ({
        url: `${project_id}?offset=${offset}&limit=${limit}`,
      }),
    }),
    projectInvitePost: build.mutation<
      { code: Uuid },
      ProjectIdParam & CreateProjectInviteRequest
    >({
      query: (body) => ({
        url: body.project_id,
        method: "POST",
        body,
      }),
    }),

    projectInviteMyGet: build.query<
      ProjectInvite | null,
      { username: string; project_name: string }
    >({
      query: ({ username, project_name }) => ({
        url: "my-invite",
        params: { username, project_name },
      }),
    }),
    projectInviteCodeGet: build.query<ProjectInvite, { code: Uuid }>({
      query: (params) => ({
        url: "",
        params,
      }),
    }),
    projectInviteUserCodePost: build.mutation<void, { code: Uuid }>({
      query: (params) => ({
        url: "",
        params,
        method: "POST",
      }),
    }),
    projectInviteCodePost: build.mutation<
      void,
      { code: Uuid } & UserAuthSignup
    >({
      query: (body) => ({
        url: "",
        params: {
          code: body.code,
        },
        method: "POST",
        body,
      }),
    }),
    projectInviteCodeDelete: build.mutation<void, { code: Uuid }>({
      query: (params) => ({
        url: "",
        params,
        method: "DELETE",
      }),
    }),
  }),
})

export const {
  useProjectInviteGetQuery,
  useProjectInvitePostMutation,

  useProjectInviteMyGetQuery,
  useProjectInviteCodeGetQuery,
  useProjectInviteUserCodePostMutation,
  useProjectInviteCodePostMutation,
  useProjectInviteCodeDeleteMutation,
} = projectInvitesApi
